import { Injectable, signal, effect } from '@angular/core';

type Theme = 'light' | 'dark' | 'auto';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'primeng-theme';

  currentTheme = signal<Theme>('auto');
  isDark = signal(false);

  constructor() {
    this.initializeTheme();

    effect(() => {
      this.applyTheme(this.isDark());
    });

    this.listenToSystemTheme();
  }

  setTheme(theme: Theme) {
    this.currentTheme.set(theme);
    localStorage.setItem(this.STORAGE_KEY, theme);
    this.updateDarkState();
  }

  toggleTheme() {
    const current = this.currentTheme();
    if (current === 'light') {
      this.setTheme('dark');
    } else if (current === 'dark') {
      this.setTheme('light');
    } else {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(systemPrefersDark ? 'light' : 'dark');
    }
  }

  private initializeTheme() {
    const storedTheme = localStorage.getItem(this.STORAGE_KEY) as Theme;
    this.currentTheme.set(storedTheme || 'auto');
    this.updateDarkState();
  }

  private updateDarkState() {
    const theme = this.currentTheme();
    if (theme === 'auto') {
      this.isDark.set(window.matchMedia('(prefers-color-scheme: dark)').matches);
    } else {
      this.isDark.set(theme === 'dark');
    }
  }

  private applyTheme(isDark: boolean) {
    document.documentElement.style.transition = 'background-color 0.3s ease, color 0.3s ease';

    if (isDark) {
      document.documentElement.classList.add('p-dark');
    } else {
      document.documentElement.classList.remove('p-dark');
    }

    setTimeout(() => {
      document.documentElement.style.removeProperty('transition');
    }, 300);
  }

  private listenToSystemTheme() {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (this.currentTheme() === 'auto') {
        this.updateDarkState();
      }
    });
  }
}
